import React, {useEffect} from "react";
import {Dialog, DialogContent} from "@material-ui/core";
import styled from "styled-components";
import {Grid, IconButton} from "@mui/material";
import {StyledH3, StyledH6, StyledP1, StyledP2, StyledP3} from "../ui-base/styles";
import {Colors} from "../../theme/colors";
import {SocialMediaIcons} from "../ui-base/social-media-icons";
import Chip from "@material-ui/core/Chip";
import {useDispatch, useSelector} from "react-redux";
import {PublisherService} from "../../services/publisher.service";
import {toggleLoader} from "../../store/global/global-slice";
import {PublisherModel} from "../../models/publisher.model";
import emptyLogo from "../../img/empty-publisher-logo.png";
import {CountryModel} from "../../models/country.model";
import {RootState} from "../../store/root-reducer";
import {ExternalLinkTypeEnum} from "../../models/external-link-type.enum";
import {useTranslation} from "react-i18next";
import {AllLanguages} from "../../data/languages";
import CloseIcon from "@material-ui/icons/Close";

export const PublisherDetailsDialog: React.FC<Props> = ({open, handleClose, publisherId, showContactData = false}) => {
    const [publisher, setPublisher] = React.useState<PublisherModel>(null);
    const dispatch = useDispatch();
    const {t} = useTranslation(['publisher', 'common']);
    const countries: CountryModel[] = useSelector(
        (state: RootState) => state.countries.countries
    );

    const getCountryByIso = (iso: string): CountryModel => {
        return countries.find(country => country.iso === iso);
    }

    const getLangByCode = (searchingCode: string) => {
        return AllLanguages.find((lang) => {
            return lang.code === searchingCode;
        });
    }

    const getExternalLinkKey = (name: string) => {
        const linkTypes: string[] = Object.keys(ExternalLinkTypeEnum);

        return linkTypes.find((linkType) => (
            ExternalLinkTypeEnum[linkType] === name
        ));
    }

    useEffect(() => {
        const loadProfile = async () => {
            dispatch(toggleLoader(true));
            const profile = await PublisherService.getProfile(publisherId);

            if (profile) {
                setPublisher(profile);
                dispatch(toggleLoader(false));
            }
        }

        if (publisherId && open) {
            loadProfile();
        }
    }, [dispatch, publisherId, open]);

    return (
        <>
            <Dialog
                fullWidth={true}
                maxWidth={'lg'}
                open={open}
                onClose={handleClose}
                style={{backgroundColor: 'transparent'}}
            >
                <StyledDialogContent>
                    <StyledCloseContainer>
                        <IconButton onClick={() => {
                            handleClose(false)
                        }}>
                            <CloseIcon/>
                        </IconButton>
                    </StyledCloseContainer>
                    <Grid container direction={"column"}>
                        <Grid item container direction={"row"} spacing={2} alignItems={"center"}>
                            <Grid item xs="auto" style={{paddingBottom: 0}}>
                                <StyledLogo src={publisher?.logo?.file || emptyLogo}/>
                            </Grid>
                            <Grid item xs style={{paddingBottom: 0}}>
                                <Grid item>
                                    <StyledH3>{publisher?.name}</StyledH3>
                                    <StyledP2>{publisher?.details?.country ? getCountryByIso(publisher?.details?.country).name : '---'}</StyledP2>
                                </Grid>

                                {/*<Grid item container direction={"row"} justifyContent="flex-start"*/}
                                {/*      style={{padding: '0.5rem 0'}}>*/}
                                {/*    <Grid item style={{display: 'flex', flexDirection: 'row', marginRight: '2rem'}}*/}
                                {/*          alignItems="center">*/}
                                {/*        <Grid item xs>*/}
                                {/*            <StyledStatsP1>{publisher?.annual_number_of_titles || '---'}</StyledStatsP1>*/}
                                {/*        </Grid>*/}
                                {/*        <Grid item xs>*/}
                                {/*            <StyledStatsP3>{t('editProfile.numberOfBooks')}</StyledStatsP3>*/}
                                {/*        </Grid>*/}
                                {/*    </Grid>*/}
                                {/*    <Grid item style={{display: 'flex', flexDirection: 'row'}} alignItems="center">*/}
                                {/*        <Grid item xs>*/}
                                {/*            <StyledStatsP1>{publisher?.average_edition_size || '---'}</StyledStatsP1>*/}
                                {/*        </Grid>*/}
                                {/*        <Grid item xs>*/}
                                {/*            <StyledStatsP3>{t('editProfile.averageNumberOfBooks')}</StyledStatsP3>*/}
                                {/*        </Grid>*/}
                                {/*    </Grid>*/}
                                {/*</Grid>*/}

                                <Grid item container direction="row" spacing={1}>
                                    {publisher?.external_links.map((externalLink) => (
                                        <Grid item key={externalLink.id}>
                                            <a href={externalLink.url} target="_blank" rel="noreferrer">
                                                <SocialMediaIcons iconName={getExternalLinkKey(externalLink.name)}/>
                                            </a>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>
                        <StyledHr/>
                        <Grid item>
                            <StyledSpan1>{t('editProfile.form.description')}</StyledSpan1>
                            <StyledDescP2>{publisher?.description || '---'}</StyledDescP2>
                        </Grid>
                        <StyledHr/>
                        <Grid item>
                            <StyledCustomH6>{t('editProfile.generalInformation')}</StyledCustomH6>
                        </Grid>
                        <Grid item container direction={"row"} style={{marginTop: '0.5rem'}}>
                            <Grid item xs={12} sm={4}>
                                <StyledSpan1>{t('editProfile.form.nip')}</StyledSpan1>
                                <StyledDescP2>{publisher?.details?.nip || '---'}</StyledDescP2>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <StyledSpan1>{t('editProfile.form.address')}</StyledSpan1>
                                <StyledDescP2>{publisher?.details?.address || '---'}</StyledDescP2>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <StyledSpan1>{t('editProfile.form.country')}</StyledSpan1>
                                <StyledDescP2>{publisher?.details?.country ? getCountryByIso(publisher?.details?.country).name : '---'}</StyledDescP2>
                            </Grid>
                        </Grid>
                        <Grid item container direction={"row"} style={{marginTop: '0.5rem'}}>
                            <Grid item xs={12} sm={12}>
                                <StyledSpan1>{t('editProfile.mainCategory')}</StyledSpan1>
                                <StyledDescP2>{publisher?.main_category ? t('common:mainCategories.' + publisher.main_category) : '---'}</StyledDescP2>
                            </Grid>
                        </Grid>
                        <Grid item container direction={"row"} style={{marginTop: '0.5rem'}}>
                            <Grid item xs={12} sm={12}>
                                <StyledSpan1>{t('editProfile.detailedCategories')}</StyledSpan1>
                                <Grid container direction={'row'}>
                                    <Grid item style={{maxWidth: '100%'}}>
                                        {publisher?.categories_wanted.map(category => (
                                            <StyledChip key={category.id} size="small" label={category.name}
                                                        style={{maxWidth: '100%'}}/>
                                        ))}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item container direction={"row"} style={{marginTop: '0.5rem'}}>
                            <Grid item xs={12} sm={12}>
                                <StyledSpan1>{t('editProfile.publishingLanguageBooks')}</StyledSpan1>
                                <Grid container direction={'row'}>
                                    <Grid item style={{maxWidth: '100%'}}>
                                        {publisher?.book_languages?.map(bookLanguage => (
                                            <StyledChip  key={bookLanguage} size="small" label={getLangByCode(bookLanguage)?.language}
                                                         style={{maxWidth: '100%'}}/>
                                        ))}

                                        {!publisher?.book_languages || publisher?.book_languages.length === 0 &&
                                            '---'
                                        }
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        <StyledHr/>

                        {showContactData &&
                            <>
                                <Grid item>
                                    <StyledCustomH6>{t('editProfile.form.contactPerson')}</StyledCustomH6>
                                </Grid>
                                <Grid item container direction={"row"} style={{marginTop: '0.5rem'}}>
                                    <Grid item xs={12} sm={4}>
                                        <StyledSpan1>{t('editProfile.form.contactPerson')}</StyledSpan1>
                                        <StyledDescP2>{publisher?.details?.contact_person || '---'}</StyledDescP2>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <StyledSpan1>{t('editProfile.form.phone')}</StyledSpan1>
                                        <StyledDescP2>{publisher?.details?.phone_prefix} {publisher?.details?.phone}</StyledDescP2>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <StyledSpan1>{t('editProfile.form.contactEmail')}</StyledSpan1>
                                        <StyledDescP2>{publisher?.details?.contact_email}</StyledDescP2>
                                    </Grid>
                                </Grid>

                                <StyledHr/>
                            </>
                        }

                        <Grid item>
                            <StyledCustomH6>{t('editProfile.offer')}</StyledCustomH6>
                        </Grid>
                        <Grid item container direction={"row"} style={{marginTop: '0.5rem'}}>
                            <Grid item xs={12} sm={4}>
                                <StyledSpan1>{t('editProfile.textServices.title')}</StyledSpan1>
                                <Grid container direction={'row'}>
                                    <Grid item style={{maxWidth: '100%'}}>
                                        {publisher?.looking_for_editing &&
                                            <StyledChip size="small" label={t('editProfile.textServices.option1')}
                                                        style={{maxWidth: '100%'}}/>
                                        }
                                        {publisher?.looking_for_translation &&
                                            <StyledChip size="small" label={t('editProfile.textServices.option2')}
                                                        style={{maxWidth: '100%'}}/>
                                        }
                                        {publisher?.looking_for_reviewing &&
                                            <StyledChip size="small" label={t('editProfile.textServices.option3')}
                                                        style={{maxWidth: '100%'}}/>
                                        }
                                        {publisher?.looking_for_ghost_writing &&
                                            <StyledChip size="small" label={t('editProfile.textServices.option4')}
                                                        style={{maxWidth: '100%'}}/>
                                        }
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <StyledSpan1>{t('editProfile.graphicServices.title')}</StyledSpan1>
                                <Grid container direction={'row'}>
                                    <Grid item style={{maxWidth: '100%'}}>
                                        {publisher?.looking_for_illustration &&
                                            <StyledChip size="small" label={t('editProfile.graphicServices.option1')}
                                                        style={{maxWidth: '100%'}}/>
                                        }
                                        {publisher?.looking_for_covers &&
                                            <StyledChip size="small" label={t('editProfile.graphicServices.option2')}
                                                        style={{maxWidth: '100%'}}/>
                                        }
                                        {publisher?.looking_for_composition &&
                                            <StyledChip size="small" label={t('editProfile.graphicServices.option3')}
                                                        style={{maxWidth: '100%'}}/>
                                        }
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <StyledSpan1>{t('editProfile.additional')}</StyledSpan1>
                                <Grid container direction={'row'}>
                                    <Grid item style={{maxWidth: '100%'}}>
                                        {publisher?.looking_for_funding &&
                                            <StyledChip size="small" label={t('editProfile.otherServices.option1')}
                                                        style={{maxWidth: '100%'}}/>
                                        }
                                        {publisher?.looking_for_bookmarketing &&
                                            <StyledChip size="small" label={t('editProfile.otherServices.option2')}
                                                        style={{maxWidth: '100%'}}/>
                                        }
                                        {publisher?.looking_for_literary_agent &&
                                            <StyledChip size="small" label={t('editProfile.otherServices.option3')}
                                                        style={{maxWidth: '100%'}}/>
                                        }
                                        {publisher?.looking_for_other &&
                                            <StyledChip size="small" label={publisher.looking_for_other}
                                                        style={{maxWidth: '100%'}}/>
                                        }
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </StyledDialogContent>
            </Dialog>
        </>
    )
        ;
}

const StyledDialogContent = styled(DialogContent)`
  position: relative;
  background: #fff;
  border: 1px solid #E0E0E0;
  padding-bottom: 20px;
`;

const StyledLogo = styled('img')`
  height: 140px;
`;

const StyledStatsP1 = styled(StyledP1)`
  font-size: 1.8rem;
`;

const StyledStatsP3 = styled(StyledP3)`
  color: ${Colors.neutral50};
  font-weight: 500;
`;

const StyledDescP2 = styled(StyledP2)`
  color: ${Colors.neutral20};
`;

const StyledHr = styled('hr')`
  width: 100%;
  border: none;
  background-color: ${Colors.neutral90};
  height: 1px;
`;

const StyledSpan1 = styled('span')`
  font-weight: 400;
  font-size: 0.8rem;
  color: ${Colors.neutral50};
`;

const StyledCustomH6 = styled(StyledH6)`
  margin: 1rem 0 0;
  color: ${Colors.primary50};
`;

const StyledChip = styled(Chip)`
  background-color: #F5F5F5;
  color: #424242;
  border: 1px solid #C2C2C2;
  font-weight: 500;
  margin-right: 0.4rem;
`;

const StyledCloseContainer = styled('div')`
  position: absolute;
  top: 1rem;
  right: 1rem;
`;

interface Props {
    open: boolean,
    publisherId: string,
    handleClose: (val: any) => void,
    showContactData: boolean
}